import React, { useState } from 'react';
import axios from 'axios';
import { biosdkOperationUrl, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import { Fingerprint, Upload, Loader2, AlertCircle, CheckCircle2, XCircle, Clock, ArrowLeftRight } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Select } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { resolveIsMatch, parseOptionalNumber } from './matchResultUtils';

function pickFingerDecision(decisions) {
  if (!decisions || typeof decisions !== 'object') return {};
  if (decisions.FINGER) return decisions.FINGER;
  if (decisions.FINGERPRINT) return decisions.FINGERPRINT;
  if (decisions.Finger) return decisions.Finger;
  const keys = Object.keys(decisions);
  const fingerKey = keys.find((k) => /^finger(print)?$/i.test(k));
  if (fingerKey) return decisions[fingerKey];
  const first = keys[0];
  return first ? decisions[first] : {};
}

function normalizeAnalyticsInfo(raw) {
  if (raw == null) return {};
  if (Array.isArray(raw)) {
    const out = {};
    for (const item of raw) {
      if (item && typeof item === 'object') {
        const k = item.key ?? item.name ?? item.k;
        const v = item.value ?? item.val ?? item.v;
        if (k != null) out[String(k)] = v;
      }
    }
    return out;
  }
  if (typeof raw === 'object') return raw;
  return {};
}

function getAnalyticsValue(analyticsObj, ...names) {
  const lower = names.map((n) => n.toLowerCase());
  for (const key of Object.keys(analyticsObj)) {
    if (lower.includes(key.toLowerCase())) {
      return analyticsObj[key];
    }
  }
  return undefined;
}

function extractFingerScoreThreshold(decision) {
  const analyticsInfo = normalizeAnalyticsInfo(
    decision.analyticsInfo ?? decision.analytics ?? decision.analyticsinfo
  );
  const score = parseOptionalNumber(
    getAnalyticsValue(analyticsInfo, 'score', 'matchScore', 'matchscore') ?? decision.score
  );
  const threshold = parseOptionalNumber(
    getAnalyticsValue(analyticsInfo, 'threshold', 'matchThreshold', 'matchthreshold') ?? decision.threshold
  );
  return { score, threshold, analyticsInfo };
}

function FingerMatcher() {
  const [sampleFile, setSampleFile] = useState(null);
  const [galleryFile, setGalleryFile] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [error, setError] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [sampleImageUrl, setSampleImageUrl] = useState(null);
  const [galleryImageUrl, setGalleryImageUrl] = useState(null);
  const [timing, setTiming] = useState({ convert1: 0, convert2: 0, convertSample: 0, convertGallery: 0, match: 0 });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [galleryExtracted, setGalleryExtracted] = useState(false);
  const [jp2isoSampleImageUrl, setjp2IsoSampleImageUrl] = useState(null);
  const [jp2isoGalleryImageUrl, setjp2IsoGalleryImageUrl] = useState(null);

  const toSignedByteJson = (arrayBuffer) => {
    const unsigned = new Uint8Array(arrayBuffer);
    const signed = Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
    return JSON.stringify(signed);
  };

  const resetState = () => {
    setMatchResult(null);
    setShowAdvanced(false);
    setError(null);
    setProcessing(false);
  };

  const convertFingerImageToIso = async (inputFile, subType) => {
    const formData = new FormData();
    formData.append('file', inputFile);
    const candidateUrls = [
      `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=${subType}`,
      `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=UNKNOWN&purpose=AUTH`,
      `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=UNKNOWN`,
    ];
    let lastError = null;
    for (const url of candidateUrls) {
      try {
        return await axios.post(url, formData, { responseType: 'arraybuffer' });
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError;
  };

  const handleSampleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setSampleFile(selectedFile);
    resetState();
    
    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let startConvertSample1 = performance.now();

    try {
      if (['jpeg', 'jpg', 'bmp', 'png', 'wsq'].includes(fileExtension)) {
        setSampleImageUrl(URL.createObjectURL(selectedFile));
      } else if (fileExtension === 'iso') {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile?modality=FINGER`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setSampleImageUrl(URL.createObjectURL(jpegBlob));
        setjp2IsoSampleImageUrl(null);
      } else if (fileExtension === 'jp2') {
        setError('JP2 is not supported in direct mode. Use jpg/jpeg/png/wsq/iso.');
        setSampleFile(null);
        setSampleImageUrl(null);
        return;
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Could not decode ISO for preview.';
      setError(msg);
      setSampleFile(null);
      setSampleImageUrl(null);
      return;
    }
    const endConvertSample1 = performance.now();
    const convertSampleDuration1 = endConvertSample1 - startConvertSample1;
    setTiming((prevTiming) => ({
      ...prevTiming,
      convert1: convertSampleDuration1,
    }));
  };

  const handleGalleryFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setGalleryFile(selectedFile);
    resetState();
    
    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let startConvertGallery1 = performance.now();

    try {
      if (galleryExtracted && fileExtension !== 'iso') {
        setError('When gallery is marked as extracted, upload only ISO for gallery.');
        setGalleryFile(null);
        setGalleryImageUrl(null);
        return;
      }
      if (['jpeg', 'jpg', 'bmp', 'png', 'wsq'].includes(fileExtension)) {
        setGalleryImageUrl(URL.createObjectURL(selectedFile));
      } else if (fileExtension === 'iso') {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile?modality=FINGER`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setGalleryImageUrl(URL.createObjectURL(jpegBlob));
        setjp2IsoGalleryImageUrl(null);
      } else if (fileExtension === 'jp2') {
        setError('JP2 is not supported in direct mode. Use jpg/jpeg/png/wsq/iso.');
        setGalleryFile(null);
        setGalleryImageUrl(null);
        return;
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Could not decode ISO for preview.';
      setError(msg);
      setGalleryFile(null);
      setGalleryImageUrl(null);
      return;
    }

    const endConvertGallery1 = performance.now();
    const convertGalleryDuration1 = endConvertGallery1 - startConvertGallery1;
    setTiming((prevTiming) => ({
      ...prevTiming,
      convert2: convertGalleryDuration1,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!sampleFile || !galleryFile) {
      setError('Please upload both sample and gallery images.');
      return;
    }
  
    setProcessing(true);
  
    let f1 = [];
    let f2 = [];
    let convertSampleDuration = 0;
    let convertGalleryDuration = 0;
    try {
      const startConvertSample = performance.now();
      const sampleFileExtension = sampleFile.name.split('.').pop().toLowerCase();
      const subType = selectedOption.split('|')[0];

      if (galleryExtracted) {
        if (['jpeg', 'jpg', 'bmp', 'png', 'wsq'].includes(sampleFileExtension)) {
          const isoResp = await convertFingerImageToIso(sampleFile, subType);
          const endConvertSample = performance.now();
          convertSampleDuration = endConvertSample - startConvertSample;
          f1 = toSignedByteJson(isoResp.data);
        } else if (sampleFileExtension === 'iso') {
          const SampleisoArrayBuffer = await sampleFile.arrayBuffer();
          const endConvertSample = performance.now();
          convertSampleDuration = endConvertSample - startConvertSample;
          f1 = toSignedByteJson(SampleisoArrayBuffer);
        } else {
          throw new Error('Unsupported sample file type for extracted gallery. Use jpg/jpeg/png/bmp/wsq/iso.');
        }
      } else if (
        sampleFileExtension === 'jpeg' ||
        sampleFileExtension === 'jpg' ||
        sampleFileExtension === 'bmp' ||
        sampleFileExtension === 'png' ||
        sampleFileExtension === 'wsq' ||
        sampleFileExtension === 'iso'
      ) {
        const SampleisoArrayBuffer = await sampleFile.arrayBuffer();
        const endConvertSample = performance.now();
        convertSampleDuration = endConvertSample - startConvertSample;
        f1 = toSignedByteJson(SampleisoArrayBuffer);
      } else {
        throw new Error('Unsupported sample file type. Use jpg/jpeg/png/bmp/wsq/iso.');
      }

      const startConvertGallery = performance.now();
      const galleryFileExtension = galleryFile.name.split('.').pop().toLowerCase();
      
      if (galleryFileExtension === 'jpeg' || galleryFileExtension === 'jpg'|| galleryFileExtension === 'bmp'|| galleryFileExtension === 'png' || galleryFileExtension === 'wsq' || galleryFileExtension === 'iso') {
        const GalleryisoArrayBuffer = await galleryFile.arrayBuffer();
        const endConvertGallery = performance.now();
        convertGalleryDuration = endConvertGallery - startConvertGallery;
        f2 = toSignedByteJson(GalleryisoArrayBuffer);
      } else {
        throw new Error('Unsupported gallery file type. Use jpg/jpeg/png/bmp/wsq/iso.');
      }

      const templateUrl = galleryExtracted
        ? '/single_finger_gallery_single_sample_inner_request.json'
        : '/gallery_not_extracted_template.json';

      const templateResponse = await fetch(templateUrl);
      if (!templateResponse.ok) {
        throw new Error(`Template fetch failed with status: ${templateResponse.status}`);
      }

      const [position, finger] = selectedOption.split('|')[1].split(' '); 
  
      let templateText = await templateResponse.text();
      templateText = templateText
        .replace('<sample_bdb>', f1)
        .replace('<gallery_Left_IndexFinger>', f2)
        .replace('Left', position) 
        .replace('IndexFinger', finger);
  
      const versionRequestTemplateResponse = await fetch('/match_request-template.json');
      if (!versionRequestTemplateResponse.ok) {
        throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);
      }
  
      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedTemplate = btoa(templateText);
      const finalRequestBody = JSON.parse(versionRequestTemplateText.replace('encoded_request', base64EncodedTemplate));
      finalRequestBody.operation = 'match';
  
      const isRawInput = sampleFileExtension !== 'iso' || galleryFileExtension !== 'iso';
      const matchUrl = biosdkOperationUrl('process', isRawInput);
      logBiosdkUiRoute('FingerMatcher', isRawInput ? 'rawRoute' : 'isoRoute', matchUrl, {
        operation: 'process',
      });
      const matchResponse = await axios.post(matchUrl, finalRequestBody, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (matchResponse.data.errors && matchResponse.data.errors.length > 0) {
        setError(`Error: ${matchResponse.data.errors[0].message}`);
        setMatchResult(null);
      } else {
        const responseItem = matchResponse?.data?.response?.response?.[0] || {};
        const decisions = responseItem.decisions || {};
        const decision = pickFingerDecision(decisions);
        const { score, threshold, analyticsInfo } = extractFingerScoreThreshold(decision);
        const isMatch = resolveIsMatch(score, threshold, decision.match);

        setMatchResult({
          score,
          threshold,
          isMatch,
        });
        setError(null);

        setTiming({
          convert1: timing.convert1,
          convert2: timing.convert2,
          convertSample: convertSampleDuration,
          convertGallery: convertGalleryDuration,
          match: analyticsInfo.matchDuration ?? decision.matchDuration ?? 0,
        });
        setShowAdvanced(true);
      }
  
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during matching.';
      setError(errorMessage);
      setMatchResult(null);
    } finally {
      setProcessing(false);
    }
  };

  const handleReset = () => {
    setSampleFile(null);
    setGalleryFile(null);
    setSampleImageUrl(null);
    setGalleryImageUrl(null);
    setSelectedOption('');
    setGalleryExtracted(false);
    setMatchResult(null);
    setError(null);
    setShowAdvanced(false);
  };

  const getFingerLabel = () => {
    if (!selectedOption) return 'Finger';
    return selectedOption.split('|')[1]?.replace(/([A-Z])/g, ' $1').trim() || 'Finger';
  };

  const formatScore = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(num)) return 'N/A';
    return num.toFixed(2);
  };

  const getProgressWidth = (score, threshold) => {
    const scoreNum = typeof score === 'number' ? score : parseFloat(score);
    const thresholdNum = typeof threshold === 'number' ? threshold : parseFloat(threshold);
    if (Number.isNaN(scoreNum) || Number.isNaN(thresholdNum)) return 0;
    const maxValue = Math.max(100, thresholdNum * 1.5, scoreNum);
    return Math.min(100, (scoreNum / maxValue) * 100);
  };

  return (
    <div className="biochq-page">
      <div className="biochq-container-wide">
        <Card className="biochq-card finger">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Fingerprint className="h-8 w-8 text-finger" />
              <CardTitle className="text-3xl font-bold text-gray-800">Finger Matcher</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              Compare two fingerprint images to verify identity using biometric matching
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="space-y-6">
              {/* Finger Type Selection */}
              <div className="max-w-md mx-auto space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-700 font-medium">Finger Type</Label>
                  <Select
                    value={selectedOption}
                    onChange={(e) => { setSelectedOption(e.target.value); resetState(); }}
                    className="w-full"
                  >
                    <option value="" disabled>Select finger type</option>
                    <option value="LEFT_INDEX_FINGER|Left IndexFinger">Left Index Finger</option>
                    <option value="LEFT_MIDDLE_FINGER|Left MiddleFinger">Left Middle Finger</option>
                    <option value="LEFT_RING_FINGER|Left RingFinger">Left Ring Finger</option>
                    <option value="LEFT_LITTLE_FINGER|Left LittleFinger">Left Little Finger</option>
                    <option value="LEFT_THUMB|Left Thumb">Left Thumb</option>
                    <option value="RIGHT_INDEX_FINGER|Right IndexFinger">Right Index Finger</option>
                    <option value="RIGHT_MIDDLE_FINGER|Right MiddleFinger">Right Middle Finger</option>
                    <option value="RIGHT_RING_FINGER|Right RingFinger">Right Ring Finger</option>
                    <option value="RIGHT_LITTLE_FINGER|Right LittleFinger">Right Little Finger</option>
                    <option value="RIGHT_THUMB|Right Thumb">Right Thumb</option>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="galleryExtracted"
                    checked={galleryExtracted}
                    onChange={(e) => { setGalleryExtracted(e.target.checked); resetState(); }}
                    className="rounded"
                  />
                  <Label htmlFor="galleryExtracted" className="text-sm text-gray-600">
                    Gallery image is already extracted
                  </Label>
                </div>
              </div>

              {/* Image Upload Section */}
              <div className="flex gap-4 items-stretch">
                <Card className="flex-1">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Sample Fingerprint</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="upload-zone-small">
                      <input
                        type="file"
                        id="file-sample"
                        accept=".jpg,.jpeg,.iso,.png,.jp2,.bmp,.wsq"
                        onChange={handleSampleFileChange}
                        className="hidden"
                      />
                      <label htmlFor="file-sample" className="cursor-pointer">
                        {sampleImageUrl ? (
                          <div className="relative">
                            <img src={sampleImageUrl} alt="Sample preview" className="w-full h-32 object-contain rounded" />
                            <Badge variant="finger" className="absolute top-1 right-1 text-xs">Sample</Badge>
                          </div>
                        ) : (
                          <>
                            <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-xs text-gray-600">Click to upload</p>
                          </>
                        )}
                      </label>
                    </div>
                    {sampleFile && <p className="text-xs text-gray-500 text-center truncate">{sampleFile.name}</p>}
                  </CardContent>
                </Card>

                <div className="flex items-center justify-center">
                  <div className="compare-icon">
                    <ArrowLeftRight className="h-6 w-6 text-primary" />
                  </div>
                </div>

                <Card className="flex-1">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Gallery Fingerprint</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="upload-zone-small">
                      <input
                        type="file"
                        id="file-gallery"
                        accept={galleryExtracted ? ".iso" : ".jpg,.jpeg,.iso,.png,.jp2,.bmp,.wsq"}
                        onChange={handleGalleryFileChange}
                        className="hidden"
                      />
                      <label htmlFor="file-gallery" className="cursor-pointer">
                        {galleryImageUrl ? (
                          <div className="relative">
                            <img src={galleryImageUrl} alt="Gallery preview" className="w-full h-32 object-contain rounded" />
                            <Badge variant="finger" className="absolute top-1 right-1 text-xs">{getFingerLabel()}</Badge>
                          </div>
                        ) : (
                          <>
                            <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-xs text-gray-600">Click to upload</p>
                          </>
                        )}
                      </label>
                    </div>
                    {galleryFile && <p className="text-xs text-gray-500 text-center truncate">{galleryFile.name}</p>}
                  </CardContent>
                </Card>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={handleSubmit}
                  disabled={!sampleFile || !galleryFile || !selectedOption || processing}
                  variant="finger"
                  size="lg"
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Matching...
                    </>
                  ) : (
                    <>
                      <Fingerprint className="mr-2 h-4 w-4" />
                      Match Fingerprints
                    </>
                  )}
                </Button>
                <Button onClick={handleReset} variant="outline" size="lg" disabled={processing}>
                  Reset
                </Button>
              </div>

              {/* Error Display */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Match Result */}
              {matchResult && (
                <div className="space-y-4">
                  <Alert variant={matchResult.isMatch ? 'success' : 'warning'}>
                    {matchResult.isMatch ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <AlertTitle className="text-lg">
                      {matchResult.isMatch ? 'Match Found!' : 'No Match'}
                    </AlertTitle>
                    <AlertDescription>
                      Score: {formatScore(matchResult.score)} (Threshold: {formatScore(matchResult.threshold)})
                    </AlertDescription>
                  </Alert>

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Score Visualization */}
                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Match Score</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="relative pt-4">
                          <div className="flex mb-2 items-center justify-between">
                            <span className="text-xs font-semibold text-gray-600">0</span>
                            <span className="text-xs font-semibold text-gray-600">
                              {parseFloat(matchResult.score) > 100 ? formatScore(matchResult.score) : '100'}
                            </span>
                          </div>
                          <div className="overflow-hidden h-4 text-xs flex rounded-full bg-gray-200">
                            <div
                              style={{ width: `${getProgressWidth(matchResult.score, matchResult.threshold)}%` }}
                              className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ${
                                matchResult.isMatch ? 'bg-green-500' : 'bg-yellow-500'
                              }`}
                            />
                          </div>
                          <p className="text-center mt-3 text-2xl font-bold text-primary">
                            {formatScore(matchResult.score)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Match Details */}
                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Match Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Finger Type:</span>
                          <Badge variant="finger">{getFingerLabel()}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Score:</span>
                          <span className="font-medium">{formatScore(matchResult.score)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Threshold:</span>
                          <span className="font-medium">{formatScore(matchResult.threshold)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Match Status:</span>
                          <Badge variant={matchResult.isMatch ? 'success' : 'warning'}>
                            {matchResult.isMatch ? 'MATCHED' : 'NOT MATCHED'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Timing Details */}
                  {showAdvanced && (
                    <Card className="timing-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Timing Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 text-sm text-center">
                          <div>
                            <p className="text-gray-600">Sample Convert</p>
                            <p className="font-medium">{timing.convertSample.toFixed(2)} ms</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Gallery Convert</p>
                            <p className="font-medium">{timing.convertGallery.toFixed(2)} ms</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Match Time</p>
                            <p className="font-medium text-primary">{timing.match} ms</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default FingerMatcher;
